// Shown via the site toast when the listing request fails; the list itself falls back to the "No Results Found" state.
const LISTING_ERROR_MESSAGE = "Something went wrong. Please try again.";

// Links addressed to this widget carry its filters as "#publications?type=...&topic=..."; returns that query, or null.
function publicationsQueryFromFragment(hash) {
    const prefix = '#publications?';
    return hash && hash.toLowerCase().startsWith(prefix) ? hash.substring(prefix.length - 1) : null;
}

const dynamicPublicationsWithFiltersApp = Vue.createApp({
    data() {
        return {
            contentTypes: typeof pwfContentTypes !== 'undefined' && Array.isArray(pwfContentTypes) ? pwfContentTypes : [],
            // { value: technical content type, name: site-facing label } pairs; the label is what goes into the URL.
            contentTypeOptions: typeof pwfContentTypeOptions !== 'undefined' && Array.isArray(pwfContentTypeOptions) ? pwfContentTypeOptions : [],
            topics: typeof pwfTopics !== 'undefined' && Array.isArray(pwfTopics) ? pwfTopics : [],
            articlesPerPage: typeof pwfArticlesPerPage !== 'undefined' && pwfArticlesPerPage > 0 ? pwfArticlesPerPage : 10,
            featuredArticleId: typeof pwfFeaturedArticleId !== 'undefined' ? pwfFeaturedArticleId : null,
            initialListing: typeof pwfInitialListing !== 'undefined' ? pwfInitialListing : null,
            articles: [],
            totalCount: 0,
            // Filter state the current articles/totalCount were loaded for; the count label follows this, not the live checkboxes.
            listingFiltersApplied: false,
            currentPage: 1,
            totalPages: 0,
            articlePages: [],
            selectedTypes: [],
            selectedTopics: [],
            searchQuery: '',
            loading: false,
            searchDebounceTimer: null,
            requestSeq: 0,
            filtersExpanded: true
        };
    },

    methods: {
        ...PublicationCardMethods,

        isDefaultState() {
            return this.selectedTypes.length === 0
                && this.selectedTopics.length === 0
                && this.searchQuery.trim().length === 0;
        },

        buildListingUrl() {
            const params = new URLSearchParams();
            const types = this.selectedTypes.length > 0 ? this.selectedTypes : this.contentTypes;

            params.set('types', types.join(','));

            if (this.selectedTopics.length > 0) {
                params.set('topics', this.selectedTopics.join(','));
            }

            const q = this.searchQuery.trim();
            if (q) {
                params.set('q', q);
            }

            // The featured article is pinned above the list in the default state, so keep it out of that page.
            if (this.isDefaultState() && this.featuredArticleId) {
                params.set('exclude', this.featuredArticleId);
            }

            params.set('page', this.currentPage);
            params.set('size', this.articlesPerPage);

            return '/publications/list?' + params.toString();
        },

        fetchArticles() {
            this.loading = true;

            const requestSeq = ++this.requestSeq;

            fetch(this.buildListingUrl())
                .then(response => {
                    if (!response.ok) throw new Error("Publications listing request failed with status " + response.status);
                    return response.json();
                })
                .then(listing => {
                    // Ignore out-of-order responses from earlier keystrokes / clicks.
                    if (requestSeq !== this.requestSeq) return;
                    this.applyListing(listing);
                    // applyListing may have issued a corrective re-fetch (stale page number); leave the spinner to that request.
                    if (requestSeq !== this.requestSeq) return;
                    this.loading = false;
                })
                .catch(() => {
                    // Network failure, non-2xx status or invalid JSON: fall back to the empty listing and tell the user.
                    if (requestSeq !== this.requestSeq) return;
                    this.applyListing(null);
                    this.loading = false;
                    if (typeof showToast === "function") showToast(LISTING_ERROR_MESSAGE, 7000, "fail");
                });
        },

        applyListing(listing) {
            this.articles = listing && Array.isArray(listing.items) ? listing.items : [];
            this.totalCount = listing && typeof listing.totalCount === 'number' ? listing.totalCount : 0;
            this.listingFiltersApplied = !this.isDefaultState();
            this.totalPages = Math.ceil(this.totalCount / this.articlesPerPage);

            // A stale page number (e.g. from the URL) past the last page snaps to the last page.
            if (this.totalPages > 0 && this.currentPage > this.totalPages) {
                this.currentPage = this.totalPages;
                this.updateUrlWithFilters();
                this.fetchArticles();
                return;
            }

            this.articlePages = pagination(this.currentPage, this.totalPages);
        },

        // Rebuild the selection from the current address (same-page filter links, browser back/forward).
        applyStateFromUrl(scroll) {
            clearTimeout(this.searchDebounceTimer);
            this.selectedTypes = [];
            this.selectedTopics = [];
            this.searchQuery = '';
            this.currentPage = 1;
            this.applyFiltersFromUrl();
            this.fetchArticles();

            if (scroll) {
                scrollOnPagination('dynamicPublicationsWithFilters');
            }
        },

        // Filter and page changes scroll back to the top of the widget; search typing does not.
        reload(resetPage, scroll = true) {
            if (resetPage) {
                this.currentPage = 1;
            }

            this.updateUrlWithFilters();
            this.fetchArticles();

            if (scroll) {
                scrollOnPagination('dynamicPublicationsWithFilters');
            }
        },

        goToPage(pageNumber) {
            if (isNaN(pageNumber) || pageNumber < 1 || pageNumber === this.currentPage) return;
            if (this.totalPages > 0 && pageNumber > this.totalPages) return;

            this.currentPage = pageNumber;
            this.reload(false);
        },

        // Checkbox change fires before v-model has updated the bound array, so read the state on the next tick.
        onFilterChanged() {
            this.$nextTick(() => this.reload(true));
        },

        onSearchInput() {
            clearTimeout(this.searchDebounceTimer);

            this.$nextTick(() => {
                if (!this.searchQuery.trim()) {
                    this.reload(true, false);
                    return;
                }

                // Debounced server search (shared Azure content search) - same results as the mobile API.
                this.searchDebounceTimer = setTimeout(() => this.reload(true, false), 350);
            });
        },

        onSearchSubmit() {
            clearTimeout(this.searchDebounceTimer);
            this.reload(true, false);
        },

        clearSearch() {
            clearTimeout(this.searchDebounceTimer);
            this.searchQuery = '';
            this.reload(true, false);
        },

        resetFilters() {
            clearTimeout(this.searchDebounceTimer);
            this.selectedTypes = [];
            this.selectedTopics = [];
            this.searchQuery = '';
            this.reload(true);
        },

        toggleFiltersExpanded() {
            this.filtersExpanded = !this.filtersExpanded;
        },

        // URL values are the user-facing names (article type label, topic name) as lower-case alphanumerics joined by spaces
        // (encoded as '+'), the same shape the courses widget uses: ?type=worldatworktoday+impact&topic=employeebenefits.
        sanitizeValue(val) {
            if (val === null || val === undefined) return '';
            return String(val).replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        },

        typeLabel(value) {
            const option = this.contentTypeOptions.find(o => o.value === value);
            return option ? option.name : value;
        },

        topicName(id) {
            const topic = this.topics.find(t => t.id === id);
            return topic ? topic.name : id;
        },

        updateUrlWithFilters() {
            let params = new URLSearchParams(window.location.search || '');

            if (this.selectedTypes.length > 0) {
                params.set('type', this.selectedTypes.map(t => this.sanitizeValue(this.typeLabel(t))).join(' '));
            } else {
                params.delete('type');
            }

            if (this.selectedTopics.length > 0) {
                params.set('topic', this.selectedTopics.map(id => this.sanitizeValue(this.topicName(id))).join(' '));
            } else {
                params.delete('topic');
            }

            if (this.searchQuery.trim().length > 0) {
                params.set('search', this.searchQuery.trim());
            } else {
                params.delete('search');
            }

            if (this.currentPage > 1) {
                params.set('page', this.currentPage);
            } else {
                params.delete('page');
            }

            let newUrl = window.location.pathname;
            const query = params.toString();
            if (query.length > 0) {
                newUrl += '?' + query;
            }

            if (window.location.hash) {
                newUrl += window.location.hash;
            }

            window.history.pushState(null, '', newUrl);
        },

        applyFiltersFromUrl() {
            const params = new URLSearchParams(window.location.search);

            let paramMap = {};
            for (const [key, value] of params.entries()) {
                paramMap[key.toLowerCase()] = value;
            }

            const type = paramMap['type'];
            if (type) {
                type.split(/[\s,]+/).forEach(val => {
                    const matched = this.contentTypes.find(t => this.sanitizeValue(this.typeLabel(t)) === val.toLowerCase());
                    if (matched && !this.selectedTypes.includes(matched)) {
                        this.selectedTypes.push(matched);
                    }
                });
            }

            const topic = paramMap['topic'];
            if (topic) {
                topic.split(/[\s,]+/).forEach(val => {
                    const matched = this.topics.find(t => this.sanitizeValue(t.name) === val.toLowerCase());
                    if (matched && !this.selectedTopics.includes(matched.id)) {
                        this.selectedTopics.push(matched.id);
                    }
                });
            }

            const search = paramMap['search'];
            if (search) {
                this.searchQuery = search;
            }

            const page = parseInt(paramMap['page'], 10);
            if (!isNaN(page) && page > 1) {
                this.currentPage = page;
            }
        }
    },

    computed: {
        filtersApplied() {
            return !this.isDefaultState();
        },

        // The featured article is excluded from the server total while it is pinned above the list, so add it back to the count the visitor sees.
        displayedCount() {
            return this.totalCount + (!this.listingFiltersApplied && this.featuredArticleId ? 1 : 0);
        },

        countLabel() {
            const noun = this.displayedCount === 1 ? 'article' : 'articles';
            return this.displayedCount + ' ' + noun + (this.listingFiltersApplied ? ' found' : '');
        },

        displayedArticles() {
            return this.articles;
        }
    },

    mounted() {
        // A "#publications?..." link from another page: move its filters into the query string, then read them as usual.
        const fragmentQuery = publicationsQueryFromFragment(window.location.hash);
        if (fragmentQuery !== null) {
            window.history.replaceState(null, '', window.location.pathname + fragmentQuery);
        }

        this.applyFiltersFromUrl();
        this.filtersExpanded = !window.matchMedia('(max-width: 991.98px)').matches;

        const hasUrlState = !this.isDefaultState() || this.currentPage > 1;

        // The server renders the default first page into pwfInitialListing; anything else is fetched on demand.
        if (!hasUrlState && this.initialListing) {
            this.applyListing(this.initialListing);
        } else {
            this.fetchArticles();
        }

        // Arriving with filters lands on the listing; wait for the load event so images above have their final size.
        if (hasUrlState || fragmentQuery !== null) {
            const scrollToListing = () => scrollOnPagination('dynamicPublicationsWithFilters');

            if (document.readyState === 'complete') {
                scrollToListing();
            } else {
                window.addEventListener('load', scrollToListing, { once: true });
            }
        }

        // Same-page "#publications?..." links apply in place: rewrite the address to query form, then read it back. Modifier and
        // middle clicks stay with the browser. (site.js already prevents default for "#" links, so defaultPrevented is not checked.)
        document.addEventListener('click', event => {
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

            const anchor = event.target.closest('a[href]');
            if (!anchor) return;

            const url = new URL(anchor.href, window.location.origin);
            if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;

            const query = publicationsQueryFromFragment(url.hash);
            if (query === null) return;

            event.preventDefault();
            window.history.pushState(null, '', url.pathname + query);
            this.applyStateFromUrl(true);
        });

        // Browser back/forward restores the filters that address carried.
        window.addEventListener('popstate', () => this.applyStateFromUrl(false));
    }
});

const dynamicPublicationsWithFiltersMountedApp = dynamicPublicationsWithFiltersApp.mount('#dynamicPublicationsWithFilters');
